import React, { useState, useEffect } from 'react';
import { 
  Package, TrendingUp, Users, Truck, DollarSign, Calendar, 
  ShoppingCart, Clock, Activity, CheckCircle, AlertCircle, RefreshCw,
  FileText, Home
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/DashboardHome.css';

const DashboardHome = ({ sessionData }) => {
  const [stats, setStats] = useState({
    customers: { total: 0, active: 0, inactive: 0, newThisMonth: 0 },
    consignments: { total: 0, pending: 0, inTransit: 0, delivered: 0, thisMonth: 0 },
    financial: { monthlyRevenue: 0, pendingPayments: 0, totalInvoiced: 0 }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentConsignments, setRecentConsignments] = useState([]);
  const [loadingConsignments, setLoadingConsignments] = useState(false);

  // Extract session data (handle both nested and direct formats)
  const session = sessionData?.session || sessionData;
  
  // ✅ Check if user is admin/superadmin
  const isAdmin = () => {
    const userType = session?.usertype?.toLowerCase();
    const isSuperAdmin = session?.issuperadmin === true || 
                         session?.issuperadmin === 1 || 
                         session?.issuperadmin === '1';
    
    console.log('🔐 User Type Check:', {
      usertype: userType,
      issuperadmin: session?.issuperadmin,
      isSuperAdmin: isSuperAdmin,
      result: isSuperAdmin || userType === 'admin' || userType === 'superadmin'
    });
    
    return isSuperAdmin || userType === 'admin' || userType === 'superadmin';
  };

  const hasFullAccess = isAdmin();

  useEffect(() => {
    if (hasFullAccess) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [hasFullAccess]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsResponse, consignmentsResponse] = await Promise.all([
        apiClient.get('/dashboard/stats.php'),
        apiClient.get('/dashboard/recent-consignments.php?limit=10')
      ]);

      if (statsResponse.data.success && statsResponse.data.data) {
        setStats(statsResponse.data.data.statistics);
      }

      if (consignmentsResponse.data.success && consignmentsResponse.data.data) {
        setRecentConsignments(consignmentsResponse.data.data.consignments);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const refreshConsignments = async () => {
    try {
      setLoadingConsignments(true);
      const response = await apiClient.get('/dashboard/recent-consignments.php?limit=10');
      
      if (response.data.success && response.data.data) {
        setRecentConsignments(response.data.data.consignments);
      }
    } catch (error) {
      console.error('Error refreshing consignments:', error);
    } finally {
      setLoadingConsignments(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const defaultColors = {
      'Pending': '#FFA500',
      'In Transit': '#2196F3',
      'Delivered': '#4CAF50',
      'Cancelled': '#F44336',
      'On Hold': '#FF9800',
      'Processing': '#9C27B0'
    };
    return defaultColors[status] || '#9E9E9E';
  };

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // ========================================
  // 🎨 SIMPLIFIED DASHBOARD FOR REGULAR USERS
  // ========================================
  if (!hasFullAccess) {
    return (
      <div className="dashboard-home">
        <div className="date-display">
          <Calendar size={20} />
          <span>{new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>  
        </div>

        {/* Welcome Section for Non-Admin Users */}
        <div className="user-welcome-section">
          <div className="welcome-card">
            <div className="welcome-icon">
              <Home size={48} />
            </div>
            <h2>Welcome to LTMS</h2>
            <p className="welcome-subtitle">
              Hello, <strong>{session?.fullname || session?.username || 'User'}</strong>
            </p>
            <p className="welcome-company">
              {session?.companyname || 'Company'} • FY {session?.yearname || 'N/A'}
            </p>
          </div>
        </div>

        {/* Quick Access Menu for Regular Users */}
        <div className="section-header">
          <h3>Quick Access</h3>
        </div>
        
        <div className="simple-menu-grid">
          <div className="simple-menu-card">
            <Package size={32} />
            <h4>My Consignments</h4>
            <p>View your consignments</p>
          </div>

          <div className="simple-menu-card">
            <FileText size={32} />
            <h4>Reports</h4>
            <p>Generate reports</p>
          </div>

          <div className="simple-menu-card">
            <Clock size={32} />
            <h4>Status Updates</h4>
            <p>Track status updates</p>
          </div>

          <div className="simple-menu-card">
            <Users size={32} />
            <h4>My Profile</h4>
            <p>View profile details</p>
          </div>
        </div>

        {/* Information Notice */}
        <div className="info-notice">
          <AlertCircle size={20} />
          <div>
            <p><strong>Limited Access</strong></p>
            <p>You have standard user access. Contact your administrator for additional permissions.</p>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // 🎨 FULL DASHBOARD FOR ADMIN/SUPERADMIN
  // ========================================
  return (
    <div className="dashboard-home">
      <div className="date-display">
        <Calendar size={20} />
        <span>{new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}</span>  
      </div>

      {loading ? (
        <div className="loading-state">
          <Activity className="loading-spinner" size={48} />
          <p>Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <AlertCircle size={48} color="#F44336" />
          <p>{error}</p>
          <button onClick={loadDashboardData} className="retry-btn">Retry</button>
        </div>
      ) : (
        <>
          {/* Customer Stats Section */}
          <div className="section-header">
            <h3>Customer Statistics</h3>
          </div>
          <div className="stats-grid">
            <div className="stat-card blue">
              <div className="stat-icon">
                <Users size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.customers.total}</h3>
                <p>Total Customers</p>
                <span className="stat-change positive">
                  {stats.customers.newThisMonth > 0 
                    ? `+${stats.customers.newThisMonth} new this month`
                    : 'No new customers this month'
                  }
                </span>
              </div>
            </div>

            <div className="stat-card green">
              <div className="stat-icon">
                <CheckCircle size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.customers.active}</h3>
                <p>Active Customers</p>
                <span className="stat-change">
                  {calculatePercentage(stats.customers.active, stats.customers.total)}% of total
                </span>
              </div>
            </div>

            <div className="stat-card orange">
              <div className="stat-icon">
                <AlertCircle size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.customers.inactive}</h3>
                <p>Inactive Customers</p>
                <span className="stat-change">
                  {stats.customers.inactive > 0 ? 'Requires attention' : 'All active'}
                </span>
              </div>
            </div>

            <div className="stat-card teal">
              <div className="stat-icon">
                <TrendingUp size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.customers.newThisMonth}</h3>
                <p>New This Month</p>
                <span className="stat-change positive">
                  {stats.customers.newThisMonth > 0 ? 'Growing steadily' : 'No growth yet'}
                </span>
              </div>
            </div>
          </div>

          {/* Operations Stats Section */}
          <div className="section-header">
            <h3>Operations Overview</h3>
          </div>
          <div className="stats-grid">
            <div className="stat-card purple">
              <div className="stat-icon">
                <Package size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.consignments.total}</h3>
                <p>Total Consignments</p>
                <span className="stat-change">All time (this year)</span>
              </div>
            </div>

            <div className="stat-card orange">
              <div className="stat-icon">
                <Clock size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.consignments.pending}</h3>
                <p>Pending Consignments</p>
                <span className="stat-change">
                  {stats.consignments.pending > 0 
                    ? `${calculatePercentage(stats.consignments.pending, stats.consignments.total)}% of total`
                    : 'All processed'
                  }
                </span>
              </div>
            </div>

            <div className="stat-card blue">
              <div className="stat-icon">
                <Truck size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.consignments.inTransit}</h3>
                <p>In Transit</p>
                <span className="stat-change">
                  {stats.consignments.inTransit > 0 
                    ? `${calculatePercentage(stats.consignments.inTransit, stats.consignments.total)}% of total`
                    : 'None in transit'
                  }
                </span>
              </div>
            </div>

            <div className="stat-card green">
              <div className="stat-icon">
                <CheckCircle size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.consignments.delivered}</h3>
                <p>Delivered</p>
                <span className="stat-change positive">
                  {calculatePercentage(stats.consignments.delivered, stats.consignments.total)}% success rate
                </span>
              </div>
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="section-header">
            <h3>This Month's Performance</h3>
          </div>
          <div className="stats-grid stats-grid-3">
            <div className="stat-card indigo">
              <div className="stat-icon">
                <Package size={32} />
              </div>
              <div className="stat-details">
                <h3>{stats.consignments.thisMonth}</h3>
                <p>Consignments This Month</p>
                <span className="stat-change positive">
                  {stats.consignments.total > 0 
                    ? `${calculatePercentage(stats.consignments.thisMonth, stats.consignments.total)}% of yearly total`
                    : 'First of the year'
                  }
                </span>
              </div>
            </div>

            <div className="stat-card teal">
              <div className="stat-icon">
                <DollarSign size={32} />
              </div>
              <div className="stat-details">
                <h3>{formatCurrency(stats.financial.monthlyRevenue)}</h3>
                <p>Monthly Revenue</p>
                <span className="stat-change positive">Total invoiced value</span>
              </div>
            </div>

            <div className="stat-card orange">
              <div className="stat-icon">
                <Clock size={32} />
              </div>
              <div className="stat-details">
                <h3>{formatCurrency(stats.financial.pendingPayments)}</h3>
                <p>Pending Payments</p>
                <span className="stat-change">
                  {stats.financial.monthlyRevenue > 0 
                    ? `${calculatePercentage(stats.financial.pendingPayments, stats.financial.monthlyRevenue)}% of revenue`
                    : 'No pending payments'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="activity-section">
            <div className="section-header">
              <h3>Recent Consignments</h3>
              <div className="header-actions">
                <button 
                  className="refresh-btn"
                  onClick={refreshConsignments}
                  disabled={loadingConsignments}
                  title="Refresh consignments"
                >
                  <RefreshCw size={16} className={loadingConsignments ? 'spinning' : ''} />
                </button>
                <button className="view-all-btn">View All</button>
              </div>
            </div>

            <div className="activity-table">
              {loadingConsignments ? (
                <div className="table-loading">
                  <Activity className="loading-spinner" size={24} />
                  <p>Refreshing...</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Consignment No</th>
                      <th>Shipper</th>
                      <th>Consignee</th>
                      <th>Packages</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentConsignments.length > 0 ? (
                      recentConsignments.map(item => (
                        <tr key={item.id}>
                          <td className="font-medium">{item.consignmentNo}</td>
                          <td>{item.shipper}</td>
                          <td>{item.consignee}</td>
                          <td className="text-center">{item.packages}</td>
                          <td>
                            <span 
                              className="status-badge"
                              style={{ 
                                backgroundColor: item.statusColor || getStatusColor(item.status)
                              }}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td>{formatDate(item.consignmentDate)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">No recent consignments found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-cards">
              <div className="action-card">
                <Package size={24} />
                <h4>New Consignment</h4>
                <p>Create a new consignment entry</p>
              </div>

              <div className="action-card">
                <Users size={24} />
                <h4>Add Customer</h4>
                <p>Register a new customer</p>
              </div>

              <div className="action-card">
                <Truck size={24} />
                <h4>Add Vehicle</h4>
                <p>Add a new vehicle to fleet</p>
              </div>

              <div className="action-card">
                <DollarSign size={24} />
                <h4>View Reports</h4>
                <p>Generate financial reports</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardHome;